/**
 * GeoEvent Server Configuration Types
 *
 * Type definitions for parsing GeoEvent Server XML export files.
 */

/**
 * Root configuration object parsed from GeoEvent Server XML export
 */
export interface GeoEventConfiguration {
  productVersion: string;
  geoEventServices: GeoEventService[];
  inputs: Input[];
  outputs: Output[];
  geoEventDefinitions: GeoEventDefinition[];
  tags: Tag[];
  folderDataStores: FolderDataStore[];
  arcgisServerConnections: ArcGISServerConnection[];
  geofences: Geofence[];
  geofenceSynchronizations: GeofenceSynchronization[];
  /**
   * Per-input outcome of the AGOL Portal Item resolution pre-pass.
   * Keyed by `Input.name`. Populated by `resolveAgolFeatureLayerItems`
   * before generation; consumed by the JSON generator and document
   * builder to record audit information.
   */
  agolResolutions?: Record<string, AgolResolution>;
}

/**
 * Outcome of an attempt to resolve a GeoEvent feature-service input
 * to an AGOL Portal Item ID via anonymous public search.
 */
export interface AgolResolution {
  status: 'resolved' | 'no-public-match' | 'error' | 'not-applicable';
  portalItemId?: string;
  /** Human-readable explanation suitable for the migration document. */
  message: string;
  /** Service URL returned by AGOL search, when resolved. */
  serviceUrl?: string;
}

/**
 * GeoEvent Service definition containing processing flow
 */
export interface GeoEventService {
  name: string;
  label: string;
  description: string;
  runningState: string;
  inputs: ServiceInput[];
  outputs: ServiceOutput[];
  nodes: ServiceNode[];
  flow: FlowConnection[];
}

/**
 * Reference to an input within a service
 */
export interface ServiceInput {
  name: string;
  label: string;
  inputRef: string;
  layout?: GeoEventLayout;
}

/**
 * Reference to an output within a service
 */
export interface ServiceOutput {
  name: string;
  label: string;
  outputRef: string;
  layout?: GeoEventLayout;
}

/**
 * Processing node within a service (filter, processor, or choice router).
 *
 * A GeoEvent `<choice>` element is not authored as a `<node>` in the source
 * XML — it lives inside `<flow>` and routes event records to different
 * destinations based on ordered conditional `<when>` clauses. The parser
 * synthesizes a ServiceNode for each choice (carrying the reserved
 * {@link CHOICE_PROCESSOR_URI} processor URI plus the parsed `choiceRoutes`)
 * so it participates in mapping, graph building, and generation just like a
 * processor — mapping to the Velocity "Route by Expressions" tool.
 */
export interface ServiceNode {
  name: string;
  label: string;
  filter?: FilterDefinition;
  processor?: ProcessorDefinition;
  /**
   * Ordered routing clauses when this node represents a GeoEvent choice
   * element. Present only on synthetic choice nodes.
   */
  choiceRoutes?: ChoiceRoute[];
  layout?: GeoEventLayout;
}

/**
 * A single ordered routing clause (`<when>`) of a GeoEvent choice element.
 * Each clause evaluates a logical condition and, when satisfied, routes the
 * event record to exactly one downstream target.
 */
export interface ChoiceRoute {
  name: string;
  label: string;
  order: number;
  condition: LogicalCondition;
  targetRef: string;
  targetLabel: string;
  /**
   * True for the choice element's `<otherwise>` (default/else) clause, which
   * carries no condition and routes every record not matched by an earlier
   * `<when>` clause. Translated to a catch-all route in Velocity Route by
   * Expressions.
   */
  isOtherwise?: boolean;
}

/**
 * GeoEvent UI Designer layout coordinates persisted in the source XML
 * for service inputs, outputs, and processing nodes.
 */
export interface GeoEventLayout {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Flow connection between components in a service
 */
export interface FlowConnection {
  fromRef: string;
  fromLabel: string;
  toRefs: FlowTarget[];
}

/**
 * Target of a flow connection
 */
export interface FlowTarget {
  ref: string;
  label: string;
}

/**
 * Input connector definition
 */
export interface Input {
  name: string;
  label: string;
  connector: string;
  connectorLabel: string;
  adapter: AdapterConfig;
  transport: TransportConfig;
  runningState: string;
}

/**
 * Output connector definition
 */
export interface Output {
  name: string;
  label: string;
  connector: string;
  connectorLabel: string;
  adapter: AdapterConfig;
  transport: TransportConfig;
  runningState: string;
}

/**
 * Adapter configuration with URI and properties
 */
export interface AdapterConfig {
  uri: string;
  properties: PropertyMap;
}

/**
 * Transport configuration with URI and properties
 */
export interface TransportConfig {
  uri: string;
  properties: PropertyMap;
}

/**
 * Map of property names to typed values
 */
export interface PropertyMap {
  [key: string]: PropertyValue;
}

/**
 * Property value with type information
 */
export interface PropertyValue {
  value: string;
  type: string;
}

/**
 * Filter definition for conditional processing
 */
export interface FilterDefinition {
  logicalCondition: LogicalCondition;
}

/**
 * Logical condition combining multiple conditions
 */
export interface LogicalCondition {
  operator: LogicalOperator;
  conditions: Array<AttributeCondition | LogicalCondition>;
}

export type LogicalOperator = 'AND' | 'OR' | 'NOT';

/**
 * Attribute-based filter condition
 */
export interface AttributeCondition {
  operand: string;
  operator: FilterOperator;
  value: string;
}

export type FilterOperator =
  | '='
  | '!='
  | '<'
  | '>'
  | '<='
  | '>='
  | 'CONTAINS'
  | 'STARTS_WITH'
  | 'ENDS_WITH'
  | 'IN'
  | 'NOT IN'
  | 'EXISTS'
  | 'NOT EXISTS'
  | 'ISNULL'
  | 'NOTNULL'
  | 'IS NULL'
  | 'IS NOT NULL'
  | 'MATCHES';

/**
 * Processor definition with URI and properties
 */
export interface ProcessorDefinition {
  uri: string;
  properties: PropertyMap;
}

/**
 * Reserved synthetic processor URI assigned to choice routers.
 *
 * GeoEvent choice elements carry no processor URI in the source XML. The
 * parser stamps this stable identifier onto the synthetic ServiceNode it
 * creates for each `<choice>` so the existing processor → matrix → generator
 * path can map it to the Velocity "Route by Expressions" tool. The functional
 * matrix keys its Choice entry on this exact string.
 */
export const CHOICE_PROCESSOR_URI = 'com.esri.ges.processor/Choice';

/**
 * GeoEvent Definition (schema for events)
 */
export interface GeoEventDefinition {
  guid: string;
  name: string;
  owner: string;
  accessType: string;
  fieldDefinitions: FieldDefinition[];
}

/**
 * Field definition within a GeoEvent Definition
 */
export interface FieldDefinition {
  name: string;
  type: FieldType;
  cardinality: Cardinality;
  tags?: FieldTag[];
  nestedFields?: FieldDefinition[];
}

export type FieldType =
  | 'String'
  | 'Integer'
  | 'Short'
  | 'Long'
  | 'Float'
  | 'Double'
  | 'Boolean'
  | 'Date'
  | 'Geometry'
  | 'Group';

export type Cardinality = 'One' | 'Many';

/**
 * Field tag for special field identification
 */
export interface FieldTag {
  name: string;
}

/**
 * Tag for categorizing components
 */
export interface Tag {
  name: string;
  label: string;
}

/**
 * Folder data store registration
 */
export interface FolderDataStore {
  name: string;
  label: string;
  path: string;
  accessType: string;
  canRead: boolean;
  canWrite: boolean;
}

/**
 * ArcGIS Server connection registration
 */
export interface ArcGISServerConnection {
  name: string;
  label: string;
  url: string;
  /** Raw connectionType attribute from the GeoEvent XML (`AGOL` | `PORTAL` | `SERVER`). */
  connectionType: string;
  /**
   * Effective connection type after applying URL/credential-based AGOL
   * detection. Customers frequently register an AGOL organization using
   * `connectionType="PORTAL"`; the effective type captures the actual target.
   */
  effectiveConnectionType?: 'AGOL' | 'PORTAL' | 'SERVER';
  /**
   * For AGOL custom-org URLs (e.g. `https://solutions.maps.arcgis.com/`),
   * the org subdomain ("solutions"). Undefined for the default `arcgis.com`
   * host or non-AGOL connections.
   */
  agolUrlKey?: string;
  tokenConfigured: boolean;
  username: string;
  password: string;
}

/**
 * Geofence definition
 */
export interface Geofence {
  id: string;
  name: string;
  category: string;
  active: boolean;
  geometry: string;
}

/**
 * Geofence synchronization rule
 */
export interface GeofenceSynchronization {
  id: string;
  label: string;
  dataStoreName: string;
  serviceName: string;
  layer: string;
  refreshInterval: number;
  customLayerUrl: string;
  nameField: string;
  categoryField: string;
  wkid: string;
  queryString: string;
  incrementalUpdate: boolean;
  referenceToLayerType: string;
  serverType: string;
  path: string;
  maxAllowableOffset: string;
}

/**
 * Union type for any GeoEvent component
 */
export type GeoEventComponent = Input | Output | ServiceNode | GeoEventService;

export type GeoEventComponentKind = 'input' | 'output' | 'service-node' | 'service';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function endpointDirection(component: Record<string, unknown>): 'input' | 'output' | undefined {
  const connector = readString(component.connector).toLowerCase();
  const adapter = isRecord(component.adapter) ? component.adapter : undefined;
  const transport = isRecord(component.transport) ? component.transport : undefined;
  const adapterUri = readString(adapter?.uri).toLowerCase();
  const transportUri = readString(transport?.uri).toLowerCase();

  if (adapterUri.includes('.outbound/') || transportUri.includes('.outbound/') || connector.includes('-out-')) {
    return 'output';
  }
  if (adapterUri.includes('.inbound/') || transportUri.includes('.inbound/') || connector.includes('-in-')) {
    return 'input';
  }
  return undefined;
}

export function getGeoEventComponentKind(component: unknown): GeoEventComponentKind | undefined {
  if (!isRecord(component)) return undefined;

  if ('inputs' in component && 'outputs' in component && 'nodes' in component && 'flow' in component) {
    return 'service';
  }
  if ('filter' in component || 'processor' in component) {
    return 'service-node';
  }
  if ('connector' in component && 'adapter' in component && 'transport' in component) {
    return endpointDirection(component);
  }
  return undefined;
}

export function isInputComponent(component: unknown): component is Input {
  return getGeoEventComponentKind(component) === 'input';
}

export function isOutputComponent(component: unknown): component is Output {
  return getGeoEventComponentKind(component) === 'output';
}

export function isServiceNodeComponent(component: unknown): component is ServiceNode {
  return getGeoEventComponentKind(component) === 'service-node';
}

export function isGeoEventServiceComponent(component: unknown): component is GeoEventService {
  return getGeoEventComponentKind(component) === 'service';
}

export function getGeoEventComponentName(component: unknown): string | undefined {
  if (!isRecord(component)) return undefined;
  return typeof component.name === 'string' ? component.name : undefined;
}

export function getGeoEventComponentLabel(component: unknown): string | undefined {
  if (!isRecord(component)) return undefined;
  return typeof component.label === 'string' ? component.label : getGeoEventComponentName(component);
}
